import { useCallback } from "react";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import { onHeroExperienceClick } from "../analytics/analytics.click-handlers";
import Colors from "../assets/constants/Colors";
import {
  heroTest
} from "../assets/images";
import { RoutePath } from "../navigation/Routing";

//TODO: Remove props and put constants here or pipe in props from Home page.
/**
 * HomeHeroProps
 * Props:
 * - title,
 * - subtitle,
 * - body
 * - ctaLabel,
 * - ctaHref,
 * - onCtaClick
 * - imageSrc,
 * - imageAlt
 */

interface HomeHeroProps {
  title?: string;
  subtitle?: string;
  body1?: string;
  ctaLabel?: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  imageSrc?: string;
  imageAlt?: string;
}

const HomeHero = ({
  title = "Hello",
  subtitle = "My name is Andrea Lopez.",
  body1 = "Over the past 6 years, I have automated multiple workflows and helped launch 10+ mobile and web apps as a software engineer for startups, small business, and large tech teams, gaining project management experience along the way.",
  ctaLabel = "Checkout my work →",
  ctaHref,
  onCtaClick,
  imageSrc = heroTest,
  imageAlt = "Profile photo of Andrea Lopez",
}: HomeHeroProps) => {
    const onLinkClick = useCallback((href: string, targetText: string) => (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      onHeroExperienceClick({href, targetText})
    },[]);;
  
  const ctaProps = ctaHref ? { href: ctaHref } : { type: "button", onClick: onCtaClick };

  return (
    <HeroGridContainer>
      <Left>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <Body>{body1}</Body>
        <Cta onClick={onLinkClick(RoutePath.Experience, ctaLabel)} to={RoutePath.Experience} {...ctaProps}>{ctaLabel}</Cta>
      </Left>

      <Right>
        <BlobWrap>
          <BlobImg src={imageSrc} alt={imageAlt} loading="lazy" />
        </BlobWrap>
      </Right>
    </HeroGridContainer>
  );
}

/* ===== styled-components ===== */;

const HeroGridContainer = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: clamp(24px, 4vw, 48px);

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Left = styled.div`
  max-width: 720px;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-weight: 800;
  letter-spacing: -1px;
  line-height: 0.95;
  /* Big like the screenshot */
  font-size: clamp(72px, 14vw, 160px);
`;

const Subtitle = styled.h2`
  margin: 8px 0 20px;
  font-weight: 600;
  font-size: clamp(24px, 3.5vw, 40px);
`;

const Body = styled.p`
  margin: 0 0 28px;
  color: #8a8a8a;
  line-height: 1.6;
  font-size: clamp(16px, 1.6vw, 18px);
  max-width: 48ch;
`;

const Cta = styled(NavLink)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 26px;
  border-radius: 999px;
  text-decoration: none;
  font-weight: 700;
  font-size: 16px;
  border: none;
  background: linear-gradient(180deg, ${Colors.globalLink} 0%, ${Colors.globalLink} 60%, ${Colors.globalLink} 100%);
  color: #fff;
  box-shadow: 0 12px 24px ${Colors.globalHoverLink};
  transition: transform 150ms ease, box-shadow 150ms ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 28px #fb9f89;
    color: #fff;
  }

  &:active {
    transform: translateY(0);
  }
`;

const Right = styled.div`
  display: flex;
  justify-content: left;
  align-items: center;
`;

const BlobWrap = styled.div``;

const BlobImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  max-width: 400px;
`;

export default HomeHero;